import React, {useState, useRef, useEffect, useCallback, useMemo} from "react";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import Pagination from "../components/Pagination";
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  UncontrolledTooltip,
} from "reactstrap";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import helpers from "utils/helpers";
import useNotificationHighlight from "hooks/useNotificationHighlight";
import { useNotification } from "providers/NotificationProvider";
import userServices from "services/userServices";
import AssignModeratorModal from "./AssignModeratorModal";
import { useAuth } from "context/AuthContext";
import { useNavigate } from "react-router-dom";
import ChatIcon from "@mui/icons-material/Chat";
import { useTranslation } from "context/TranslationProvider";
import CloseIcon from "@mui/icons-material/Close";

function SupportTicketsTable({items, setItems, loading, meta, tableState, search, onSearch, onTableChange, deleteRow, statusLoadingId}) { 
  const { t, language } = useTranslation();
  const { isHighlighted } = useNotificationHighlight();
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [assignTicket, setAssignTicket] = useState(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [resolveTicket, setResolveTicket] = useState(null);
  const [isResolutionModalOpen, setIsResolutionModalOpen] = useState(false);
  const [resNotes, setResNotes] = useState("");
  const [isResolving, setIsResolving] = useState(false);
  const { permission } = useAuth();
  const supportPermission = permission?.permissions?.modules?.supportTickets;
  const componentRef = useRef(null);
  const { notify } = useNotification();
  const navigate = useNavigate();

  const lastLimit = useRef(tableState?.limit || 20);

  useEffect(() => {
    if (tableState?.limit) {
      lastLimit.current = tableState.limit;
    }
  }, [tableState?.limit]);

  const handlePaginationChange = (page, rowsPerPage) => {
    lastLimit.current = rowsPerPage;
    if (onTableChange) {
      onTableChange("pagination", { page: page + 1, sizePerPage: rowsPerPage });
    }
  };

  const handleSortChange = (sortField, sortOrder) => {
    if (onTableChange) {
      onTableChange("sort", { sortField, sortOrder });
    }
  };

  const openAssignModal = (ticket) => {
    setAssignTicket(ticket);
    setIsAssignModalOpen(true);
  };

  const closeAssignModal = () => {
    setAssignTicket(null);
    setIsAssignModalOpen(false);
  };

  const getTicketDetails = useCallback((row) => {
    if (!row?._id) return;
    const source = row.source === "Dispute" ? "Dispute" : "SupportRequest";
    const path = `/admin/support-tickets/${row._id}/details?source=${source}`; 
    navigate(path);
  }, [navigate]);

  const goToConversation = (item) => {
    if (!item?._id || !item?.source) return;
    let type = item.source.toLowerCase();
    if (type === "supportrequest") type = "support-ticket";
    if (type === "dispute") type = "dispute";

    navigate(`/admin/conversation/${type}/${item._id}`);
  };
 

  const handleStatusChange = async (row, status) => {
    const ticketId = row._id;
    if (updatingStatus[ticketId]) return;

    if (status === "resolved") {
      setResolveTicket(row);
      setResNotes(row.resolutionNotes || "");
      setIsResolutionModalOpen(true);
      return;
    }

    setUpdatingStatus((prev) => ({ ...prev, [ticketId]: true }));
    try {
      let res;
      if (row.source === "Dispute") {
        res = await userServices.updateDisputeRequest(ticketId, { status }, false);
      } else {
        res = await userServices.updateSupportRequest(ticketId, { status });
      }

      const resPayload = res?.data || res;
      if (resPayload?.success !== false) {
        notify("success", t(resPayload?.messageKey || "page.support-tickets.toast.statusUpdated", resPayload?.message || "Ticket status updated successfully"));
        
        // Instant local update
        if (setItems) {
          setItems(prev => prev.map(item => 
            item._id === ticketId ? { ...item, status: status } : item
          ));
        }

        // Trigger server refresh to ensure sorting/pagination remains correct
        if (onTableChange) {
          onTableChange("refresh", {});
        }

      } else {
        notify("error", t(resPayload?.messageKey || resPayload?.error || "page.support-tickets.toast.statusUpdateFailed", resPayload?.message));
      }
    } catch (err) {
      notify("error", t("page.support-tickets.toast.statusUpdateFailed", "Failed to update ticket resolution details."));
    } finally {
      setUpdatingStatus((prev) => {
        const next = { ...prev };
        delete next[ticketId];
        return next;
      });
    }
  };

  const closeResolutionModal = () => {
    setResolveTicket(null);
    setResNotes("");
    setIsResolutionModalOpen(false);
  };

  const handleResolutionSubmit = async () => {
    if (!resolveTicket) return;
    if (!resNotes.trim()) {
      notify("error", t("page.support-tickets.toast.resolutionNotesRequired", "Resolution notes are required."));
      return;
    }
    const ticketId = resolveTicket._id;
    setIsResolving(true);
    try {
      const payload = {
        status: "resolved",
        resolutionNotes: resNotes.trim(),
      };
      let res;
      if (resolveTicket.source === "Dispute") {
        res = await userServices.updateDisputeRequest(ticketId, payload, false);
      } else {
        res = await userServices.updateSupportRequest(ticketId, payload);
      }

      const resPayload = res?.data || res;
      if (resPayload?.success !== false) {
        notify("success", t("page.support-tickets.toast.statusUpdated", "Ticket status updated successfully"));
        
        if (setItems) {
          setItems(prev => prev.map(item => 
            item._id === ticketId ? { ...item, status: "resolved", resolutionNotes: resNotes.trim() } : item
          ));
        }

        if (onTableChange) {
          onTableChange("refresh", {});
        }
        closeResolutionModal();
      } else {
        const errorKey = typeof resPayload?.error === "string" ? resPayload.error : "page.support-tickets.toast.statusUpdateFailed";
        const messageVal = typeof resPayload?.message === "string" ? resPayload.message : t("page.support-tickets.toast.statusUpdateFailed");
        notify("error", t(resPayload?.messageKey || errorKey, messageVal));
      }
    } catch (err) {
      notify("error", t("page.support-tickets.toast.statusUpdateFailed"));
    } finally {
      setIsResolving(false);
    }
  };


  const rowEvents = useMemo(() => ({
    onClick: (e, row) => {
      const target = e.target;
      if (
        target.closest("button") ||
        target.closest(".dropdown") ||
        target.closest(".dropdown-menu") ||
        target.closest(".dropdown-toggle") ||
        target.closest("a") ||
        target.closest("input")
      ) {
        return;
      }
      if (supportPermission?.read) {
        getTicketDetails(row);
      }
    },
  }), [supportPermission?.read, getTicketDetails]);

  const columns = [
    { 
      dataField: "title", 
      text: t("table.headers.title", "Title"),
      headerAlign: "left",
      align: "left", 
      sort: true,
      formatter: (cell, row) => {
        const titleId = `ticket-title-${row._id}`;
        return (
          <>
            <span
              id={titleId}
              className={`font-weight-600 ${supportPermission?.read ? "cursor-pointer" : ""}`}
            >
              {cell || "-"}
            </span>
            <UncontrolledTooltip
              key={`tooltip-title-${row._id}-${language}`}
              target={titleId}
              trigger="hover"
            >
              {supportPermission?.read
                ? t("tooltip.viewTicket", "Click to view ticket details")
                : t("tooltip.viewNotAllowed", "View not allowed")}
            </UncontrolledTooltip>
          </>
        );
      },
    },
    {
      dataField: "user",
      text: t("table.headers.userName", "User Name"),
      headerAlign: "left",
      align: "left",
      formatter: (_, row) =>
        row.user ? `${row.user.firstName} ${row.user.lastName}` : "-",
    },
    {
      dataField: "source",
      text: t("table.headers.type", "Type"),
      headerAlign: "left",
      align: "left",
      formatter: (cell) => (cell === "Dispute" ? t("page.messages.disputeLabel", "Dispute") : t("page.messages.supportTicketLabel", "Support Ticket")),
    },
    {
      dataField: "status",
      text: t("table.headers.status", "Status"),
      align: "left",
      headerAlign: "left",
      formatter: (cell, row, rowIndex, formatExtraData) => {
        const { updatingStatus, statusLoadingId } = formatExtraData || {};
        const currentStatus = (row.status || "pending").toLowerCase();
        const statusClass = helpers.getStatusClass(currentStatus, "ticket");
        const isUpdating = !!updatingStatus?.[row._id];
        const disabled = !supportPermission?.update || isUpdating || statusLoadingId === row._id || currentStatus === "resolved";
        return (
          <UncontrolledDropdown>
            <DropdownToggle
              caret
              size="sm"
              key={`ticket-status-${row._id}-${language}`}
              className={`status-dropdown ${statusClass} ${disabled ? "cursor-not-allowed opacity-75" : ""}`}
              disabled={disabled}
            >
              {t(`page.support-tickets.status.${currentStatus}`)}
            </DropdownToggle>
            <DropdownMenu>
              {Object.keys(helpers.TICKET_STATUSES).map((status) => (
                <DropdownItem
                  key={status}
                  disabled={
                    status === currentStatus || !supportPermission?.update
                  }
                  onClick={() =>
                    supportPermission?.update && handleStatusChange(row, status)
                  }
                >
                  {t(`page.support-tickets.status.${status}`)}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </UncontrolledDropdown>
        );
      },
      formatExtraData: { updatingStatus, statusLoadingId },
    },
    {
      dataField: "assignedTo",
      text: t("table.headers.assignedModerator", "Assigned Moderator"),
      headerAlign: "left",
      align: "left",
      formatter: (_, row, rowIndex, formatExtraData) => {
        const { statusLoadingId } = formatExtraData || {};
        const isAssigned = !!row.assignedTo;
        const name = isAssigned
          ? `${row.assignedTo.firstName || ""} ${row.assignedTo.lastName || ""}`.trim()
          : t("page.support-tickets.unassigned", "Unassigned");

        const tooltipText = isAssigned
          ? t("tooltip.ticketAssignmentUpdate", "Click to change assigned moderator")
          : t("tooltip.ticketAssignment", "Click to assign a moderator");

        return (
          <span className="d-inline-block">
            <Button
              id={`assign-${row._id}`}
              color="link"
              className={`p-0 text-left font-weight-bold ${
                isAssigned ? "text-success" : "text-warning"
              }`}
               onClick={() => supportPermission?.update && openAssignModal(row)}
              disabled={!supportPermission?.update || statusLoadingId === row._id}
            >
              {name}
            </Button>

            <UncontrolledTooltip
              key={`assign-tooltip-${row._id}-${language}`}
              target={`assign-${row._id}`}
              trigger="hover"
            >
              {!supportPermission?.update ? t("tooltip.notAllowed", "You don't have permission to perform this action") : tooltipText}
            </UncontrolledTooltip>
          </span>
        );
      },
      formatExtraData: { statusLoadingId },
    },
    {
      dataField: "actions",
      text: t("table.headers.actions", "Actions"),
      headerAlign: "left",
      align: "left",
      formatter: (_, row, rowIndex, formatExtraData) => {
        const { statusLoadingId } = formatExtraData || {};
        return (
          <div className="d-flex align-items-center gap-1">
            {/* View Button */}
            <Button
              id={`view-${row._id}`}
              size="sm"
              disabled={!supportPermission?.read}
              onClick={() => supportPermission?.read && getTicketDetails(row)}
            >
              <VisibilityIcon fontSize="small" color={!supportPermission?.read ? "primary" : "primary"} />
            </Button>
            <UncontrolledTooltip
              target={`view-${row._id}`}
              trigger="hover"
            >
              {!supportPermission?.read
                ? t("tooltip.viewNotAllowed", "View not allowed")
                : t("tooltip.viewTicket", "View ticket details")}
            </UncontrolledTooltip>

            {/* Conversation Button */}
            <Button
              id={`chat-${row._id}`}
              size="sm"
              onClick={() => goToConversation(row)}
            >
              <ChatIcon fontSize="small" color="info" />
            </Button>
            <UncontrolledTooltip target={`chat-${row._id}`}>
              {t("page.messages.supportConversationTitle", "Conversations For Support Ticket")}
            </UncontrolledTooltip>

            {/* Delete Button */}
             <Button
              id={`delete-${row._id}`}
              size="sm"
              disabled={!supportPermission?.delete || statusLoadingId === row._id}
              onClick={() => supportPermission?.delete && deleteRow(row)}
            >
              <DeleteIcon fontSize="small" color={!supportPermission?.delete ? "error" : "error"} />
            </Button>
            <UncontrolledTooltip
              target={`delete-${row._id}`}
              trigger="hover"
            >
              {!supportPermission?.delete ? t("tooltip.deleteNotAllowed", "Delete not allowed") : t("tooltip.deleteTicket", "Delete ticket")}
            </UncontrolledTooltip>
          </div>
        );
      },
      formatExtraData: { statusLoadingId },
    }
  ];

  return (
    <>
        <ToolkitProvider key={language} keyField="_id" data={items} columns={columns} search>
          {(props) => (
            <div className="py-4 w-full flex flex-col" ref={componentRef}>
              <div className="dataTables_filter px-4 pb-3 d-flex items-center justify-between align-items-center">
                  <span className="text-sm font-medium text-slate-600">
                    {t("table.search", "Search")}:
                  </span>
                  <div className="position-relative d-flex align-items-center" style={{ width: "100%", maxWidth: "220px" }}>
                    <input
                      className="form-control form-control-sm"
                      placeholder={t("table.search", "Search")}
                      value={search || ""}
                      onChange={(e) => {
                        if (onSearch) onSearch(e.target.value);
                      }}
                      style={{ paddingRight: "24px" }}
                    />
                    {search && (
                      <span
                        onClick={() => {
                          if (onSearch) onSearch("");
                        }}
                        className="position-absolute cursor-pointer search-clear-btn"
                      >
                        <CloseIcon style={{ fontSize: "14px" }} />
                      </span>
                    )}
                  </div>
              </div>
              <div className="table-responsive">
                <BootstrapTable
                  {...props.baseProps}
                  data={items}
                  bootstrap4
                  bordered={false}
                  rowClasses={(row) => {
                    const isDisp = row.source === "Dispute";
                    const type = isDisp ? "dispute" : "support_ticket";
                    const highlighted =
                      isHighlighted(type, row._id) ||
                      isHighlighted("support_ticket", row._id) ||
                      isHighlighted("dispute", row._id) ||
                      isHighlighted("conversation", row._id) ||
                      isHighlighted("message", row._id) ||
                      (row.conversationId && isHighlighted("conversation", row.conversationId)) ||
                      (row.ticketNumber && (isHighlighted(type, row.ticketNumber) || isHighlighted("support_ticket", row.ticketNumber)));
                    const baseClass = supportPermission?.read ? "cursor-pointer" : "";
                    return `${baseClass} ${highlighted ? "bg-[#F0F7FF] border-left-highlight" : ""}`.trim();
                  }}
                  rowEvents={rowEvents}
                  remote={{ sort: true }}
                  onTableChange={(type, { sortField, sortOrder }) => {
                    if (type === "sort") handleSortChange(sortField, sortOrder);
                  }}
                  wrapperClasses="max-h-[60vh] overflow-y-auto"
                  noDataIndication={() => (
                    <div className="text-center text-slate-500 py-4">
                      {loading ? t("common.loading", "Loading....") : t("page.support-tickets.notickets", "No Tickets Found")}
                    </div>
                  )}
                />
              </div>
              <Pagination
                totalRows={meta?.totalItems || 0}
                rowsPerPage={tableState?.limit || 20}
                currentPage={(tableState?.page || 1) - 1}
                onPageChange={(page) => handlePaginationChange(page, lastLimit.current)}
                onRowsPerPageChange={(size) => handlePaginationChange(0, size)}
                t={t}
              />
            </div>
          )}
        </ToolkitProvider>
      {/* Proper modal wrapper */}

      <Modal
        isOpen={isAssignModalOpen}
        toggle={closeAssignModal}
        backdrop="static"
        className="modal-dialog-centered"
      >
        {assignTicket && (
          <AssignModeratorModal
            ticket={assignTicket}
            onClose={closeAssignModal}
            onAssigned={(assignedUser) => {
              if (setItems) {
                setItems(prev => prev.map(item => 
                  item._id === assignTicket._id ? { ...item, assignedTo: assignedUser } : item
                ));
              }
              // Trigger server refresh
              if (onTableChange) {
                onTableChange("refresh", {});
              }
              closeAssignModal();
            }}
          />

        )}
      </Modal>

      {/* Resolution Notes Modal */}
      <Modal isOpen={isResolutionModalOpen} toggle={closeResolutionModal} centered size="md">
        <ModalHeader toggle={closeResolutionModal}>
          {t("page.support-tickets.resolutionNotesHeader", "Resolution Notes")}
        </ModalHeader>
        <ModalBody>
          <div className="alert alert-info py-2 px-3 mb-3 mt--4 border-0 d-flex align-items-center" style={{ backgroundColor: "#EFF6FF", color: "#1D4ED8", borderRadius: "8px", fontSize: "12px" }}>
            <span>
              {t("page.support-tickets.resolutionNotesNotice", "Please document the final outcome, reasoning, and resolution steps. These notes will be saved as the official record for this ticket.")}
            </span>
          </div>
          <div className="mb-3">
            <label className="form-control-label font-weight-bold mb-2 d-block text-slate-800">
              {t("page.support-tickets.resolutionNotes", "Resolution Notes")}
            </label>
            <textarea
              className="form-control"
              rows={5}
              value={resNotes}
              onChange={(e) => setResNotes(e.target.value)}
              disabled={isResolving}
              placeholder={t("page.support-tickets.resolutionPlaceholder", "Document the reasoning, evidence assessed, and resolution steps taken here...")}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeResolutionModal} disabled={isResolving}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button className="bg-warning text-white font-weight-bold" onClick={handleResolutionSubmit} disabled={isResolving}>
            {isResolving ? (
              <>
                <Spinner size="sm" className="mr-2" /> {t("common.confirm", "Confirm")}
              </>
            ) : (
              t("common.confirm", "Confirm")
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default SupportTicketsTable;
